import { useCallback, useContext, useDebugValue, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import core, { API } from '@gin-betting/core';
import filters from '@gin-betting/filters';

import { useAppliedFilters, useMemoSelector, usePrevious } from 'hooks';
import ui from 'modules/ui';
import { ALL, OverlayType, SPORTS_ORGANIZATION } from '../../../constants';
import type { LocationInfo, TournamentInfo } from '../../../types';
import { getItemsByLocation } from '../../../utils';
import { MobileFiltersCtx } from '../MobileFiltersCtx';

interface Output {
    sportItem?: API.CategorizerItem;
    closeOverlay: VoidFunction;
    isClassic: boolean;
    tournamentsWithLocation: TournamentInfo[];
    tournamentsWithoutLocation: API.CategorizerItem[];
    activeMobileCountryKey: string;
    isGroupedByOrganization: boolean;
    makeHandleClickAll: (tournamentIds: string[]) => VoidFunction;
}

const {
    constants: { SportType },
    entities: {
        selectors: { selectSports },
    },
    utils: { setSearchString, getSportTypeBySport },
} = core;

const {
    selectors: { selectCategorizerData },
} = filters;

const {
    selectors: { selectActiveMobileCountryKey },
    actions: { setActiveMobileCountryKey },
} = ui;

const DEFAULT_ITEMS = {
    tournamentsWithLocation: [],
    tournamentsWithoutLocation: [],
};

const useTournamentList = (): Output => {
    const dispatch = useDispatch();
    const { setOverlay } = useContext(MobileFiltersCtx);
    const { sportId } = useAppliedFilters();

    const { formatMessage } = useIntl();

    const categorizerData = useSelector(selectCategorizerData);

    const activeMobileCountryKey = useMemoSelector(
        selectActiveMobileCountryKey
    );

    const sportItem = sportId
        ? categorizerData.find(i => i.value === sportId)
        : undefined;

    const closeOverlay = useCallback(() => {
        setOverlay(OverlayType.NONE);
    }, [setOverlay]);

    const isGroupedByOrganization =
        !!sportItem && SPORTS_ORGANIZATION.includes(sportItem.value);

    const prevSportId = usePrevious(sportId);

    const sports = useMemoSelector(selectSports, []);
    const sport = sportItem && sports.get(sportItem.value);
    const isClassic = getSportTypeBySport(sport) === SportType.Classic;

    const partitionTournaments = useCallback(
        (categorizerItem: API.CategorizerItem | undefined): LocationInfo => {
            if (!categorizerItem) return DEFAULT_ITEMS;

            if (!isClassic) {
                return {
                    tournamentsWithLocation: [],
                    tournamentsWithoutLocation: categorizerItem.children,
                };
            }

            return getItemsByLocation({
                tournaments: categorizerItem.children,
                sportId: categorizerItem.value,
                formatMessage,
            });
        },
        [isClassic, formatMessage]
    );

    const makeHandleClickAll = useCallback(
        (tournamentIds: string[]) => () => {
            setSearchString({
                sportId,
                tournamentIds,
            });
            closeOverlay();
            dispatch(setActiveMobileCountryKey(ALL));
        },
        [closeOverlay, dispatch, sportId]
    );

    useEffect(() => {
        if (sportId && prevSportId && sportId !== prevSportId) {
            setSearchString({
                sportId,
                tournamentIds: undefined,
            });
        }
    }, [prevSportId, sportId]);

    useDebugValue({
        sportItem,
        isGroupedByOrganization,
    });

    const {
        tournamentsWithLocation,
        tournamentsWithoutLocation,
    } = partitionTournaments(sportItem);

    return {
        sportItem,
        closeOverlay,
        isClassic,
        tournamentsWithLocation,
        tournamentsWithoutLocation,
        activeMobileCountryKey,
        isGroupedByOrganization,
        makeHandleClickAll,
    };
};

export default useTournamentList;
